
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";
import { DiaryEntry } from "@/utils/diary";
import { useMemo, memo } from "react";

interface EntriesCalendarProps {
  entries: DiaryEntry[];
  selectedDate: Date | undefined;
  setSelectedDate: (date: Date | undefined) => void;
}

// Memoize the component to prevent unnecessary re-renders
const EntriesCalendar = memo(({ entries, selectedDate, setSelectedDate }: EntriesCalendarProps) => {
  const { language } = useLanguage();
  
  // Memoize the dates with entries for better performance
  const datesWithEntries = useMemo(() => {
    // Skip if no entries
    if (!entries.length) return [];
    
    // Create a map to track dates with entries more efficiently
    const datesSet = new Set(
      entries.map(entry => {
        const date = entry.date ? new Date(entry.date) : new Date(entry.createdAt);
        return format(date, 'yyyy-MM-dd');
      })
    );
    
    // Convert back to Date objects for the calendar
    return Array.from(datesSet).map(dateStr => new Date(dateStr));
  }, [entries]);
  
  return (
    <div className="border rounded-lg p-4 h-full shadow-sm transition-all duration-300 hover:shadow-md">
      <h2 className="text-lg font-medium mb-2 flex items-center text-primary">
        <CalendarIcon className="h-4 w-4 mr-2" />
        {t('entries.entryCalendar', language)}
      </h2>
      <div className="w-full overflow-x-auto">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={setSelectedDate}
          className="w-full max-w-full"
          modifiers={{
            hasEntry: datesWithEntries
          }}
          modifiersStyles={{
            hasEntry: {
              fontWeight: 'bold',
              backgroundColor: 'var(--accent)',
              borderRadius: '100%',
              color: 'var(--accent-foreground)'
            },
          }}
        />
      </div>
      {selectedDate && (
        <div className="mt-2 flex justify-end">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setSelectedDate(undefined)}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            {t('entries.clearFilter', language)}
          </Button>
        </div>
      )}
    </div>
  );
});

EntriesCalendar.displayName = "EntriesCalendar";

export default EntriesCalendar;
